import { type Span, trace } from "@opentelemetry/api";
import cuid from "cuid";

import { createLogger } from "@/logger";

const log = createLogger("telemetry");

import type React from "react";
import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";

import LoadingScreen from "@/components/LoadingScreen";
import { config } from "@/config.ts";
import { AuthContext } from "@/models/StateProviders/AuthProvider.tsx";
import { PerformanceMetricsService } from "@/services/PerformanceMetricsService";
import { clampActionDuration, TELEMETRY_CATEGORY_ATTRIBUTE } from "@/services/telemetry";

/**
 * Actions that measure perceived end-user experience; their spans carry the (unsampled-by-default)
 * `perceived` category. Every other action — per-page db writes, warmup sub-steps, feed item
 * loading — is drill-down detail and ships as `diagnostic` (off by default, LD-switchable).
 */
const PERCEIVED_ACTIONS = new Set(["feed.navigate", "feed.initial", "bootstrap.full", "bootstrap.load", "warmup.full"]);

/**
 * Sampling category for a span's creation-time attributes; sampling decisions happen in
 * `startSpan`, so the category must be present at creation rather than set afterwards.
 */
function creationAttributes(
  action: string,
  target: string | undefined,
  customAttributes?: Record<string, any>,
): Record<string, any> {
  return {
    action,
    ...(target ? { target } : {}),
    [TELEMETRY_CATEGORY_ATTRIBUTE]: PERCEIVED_ACTIONS.has(action) ? "perceived" : "diagnostic",
    ...customAttributes,
  };
}

interface ActiveSpanInfo {
  action: string;
  target?: string;
  startTime: number;
  span: Span;
}

interface TelemetryContextProps {
  setAttribute: (key: string, value: string) => void;
  trackAction: (props: { action: string; target?: string }, customAttributes?: Record<string, any>) => Span;
  finishAction: (props: { action: string; target?: string }, customAttributes?: Record<string, any>) => void;
}

export const TelemetryContext = createContext<TelemetryContextProps | undefined>(undefined);

const attributes = new Map<string, string>();
const activeSpans = new Map<string, ActiveSpanInfo>();

attributes.set("buildVersion", config.buildVersion);
attributes.set("buildSha", config.buildSha);
attributes.set("buildRef", config.buildRef);
attributes.set("buildTags", config.buildTags?.join(","));
attributes.set("buildAuthor", config.buildAuthor);
attributes.set("buildMessage", config.buildMessage);
attributes.set("buildDate", config.buildDate);
attributes.set("webRc", config.webRc);
attributes.set("webRelease", config.webRelease);

interface ItemTelemetry {
  feedId: string;
  id: string;
}
interface FeedTelemetry {
  id: string;
}

function targetForItem(item: ItemTelemetry) {
  return `feed#${item.feedId}\nitem#${item.id}`;
}
function targetForFeed(feed: FeedTelemetry) {
  return `feed#${feed.id}`;
}

export const actions = {
  feedNavigate: (feedId: string) => ({
    action: "feed.navigate",
    target: `feed#${feedId}`,
  }),

  feedItemTextLoading: (item: ItemTelemetry) => ({
    action: "feed.item.text.loading",
    target: targetForItem(item),
  }),

  feedItemAudioLoading: (item: ItemTelemetry) => ({
    action: "feed.item.audio.loading",
    target: targetForItem(item),
  }),

  feedItemTextTts: (item: ItemTelemetry) => ({
    action: "feed.item.text.tts",
    target: targetForItem(item),
  }),

  feedItemAudioTts: (item: ItemTelemetry) => ({
    action: "feed.item.audio.tts",
    target: targetForItem(item),
  }),

  feedInitial: (feed: FeedTelemetry) => ({
    action: "feed.initial",
    target: targetForFeed(feed),
  }),

  feedScroll: (feed: FeedTelemetry) => ({
    action: "feed.scroll",
    target: targetForFeed(feed),
  }),

  bootstrapLoad: () => ({ action: "bootstrap.load" }),

  bootstrapFull: () => ({ action: "bootstrap.full" }),

  bootstrapDbWrite: (name: string, page: number) => ({
    action: "bootstrap.dbwrite",
    target: `${name}\npage ${page}`,
  }),

  bootstrapAfterHooksInitialUnreadItems: () => ({
    action: "bootstrap.afterHooks.initialUnreadItems",
  }),

  warmupCalculate: () => ({
    action: "warmup.calculate",
  }),

  warmupFetchItems: (feedId?: string, feedName?: string) => ({
    action: "warmup.fetch_items",
    target: feedId ? `feed#${feedId}${feedName ? `\n${feedName}` : ""}` : undefined,
  }),

  warmupFetchContent: () => ({
    action: "warmup.fetch_content",
  }),

  warmupFull: () => ({
    action: "warmup.full",
  }),
};

export const TelemetryProvider: React.FC<{ children: ReactNode; active?: boolean }> = ({ children, active = true }) => {
  const { loggedIn } = useContext(AuthContext);
  const params = useParams();

  useEffect(() => {
    if (!active) return;
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") {
        for (const spanInfo of activeSpans.values()) {
          spanInfo.span.setAttribute("tabLostFocus", true);
        }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [active]);

  const setAttribute = useCallback((key: string, value: string) => {
    attributes.set(key, value);
  }, []);

  const trackAction = useCallback(
    ({ action, target }: { action: string; target?: string }, customAttributes?: Record<string, any>) => {
      if (!active) {
        return trace.getTracer("noop-tracer").startSpan("noop");
      }
      const spanName = `${action}#${target}`;
      if (activeSpans.has(spanName)) {
        return activeSpans.get(spanName).span;
      }
      const tracer = trace.getTracer("action-tracer");
      const span = tracer.startSpan(`Action ${action}`, {
        attributes: creationAttributes(action, target, customAttributes),
      });
      activeSpans.set(spanName, {
        action,
        target,
        startTime: Date.now(),
        span,
      });
      return span;
    },
    [active],
  );

  const finishAction = useCallback(
    ({ action, target }: { action: string; target?: string }, customAttributes: Record<string, any> = {}) => {
      if (!active) return;
      const spanName = `${action}#${target}`;
      if (!activeSpans.has(spanName)) return;

      const spanInfo = activeSpans.get(spanName);
      const span = spanInfo.span;
      // Clamp suspension artifacts: a backgrounded tab can hold a span open for hours and report
      // a duration no user experienced. The clamp caps the recorded duration at a sane ceiling
      // while `overflow=true` preserves the signal that the raw value ran past it.
      const { endTime, durationMs: duration, overflow } = clampActionDuration(spanInfo.startTime, Date.now());

      for (const [key, value] of attributes.entries()) {
        span.setAttribute(key, value);
      }
      for (const [key, value] of Object.entries(customAttributes)) {
        span.setAttribute(key, value);
      }
      if (overflow) span.setAttribute("overflow", true);
      span.end(endTime);
      activeSpans.delete(spanName);

      PerformanceMetricsService.recordMetric({
        id: cuid(),
        action,
        target,
        startTime: spanInfo.startTime,
        endTime,
        duration,
        customAttributes: overflow ? { ...customAttributes, overflow: true } : customAttributes,
      }).catch((err) => {
        log.warn("Failed to record performance metric:", err);
      });
    },
    [active],
  );

  useEffect(() => {
    const workspaceId = params.workspaceId;
    const feedId = params.feedId;
    setAttribute("workspaceId", workspaceId);
    setAttribute("feedId", feedId);
  }, [params, setAttribute]);

  // Stable context value so consumers keyed on the telemetry object don't re-run their effects on every
  // provider render.
  const contextValue = useMemo(
    () => ({ setAttribute, trackAction, finishAction }),
    [setAttribute, trackAction, finishAction],
  );

  return (
    <TelemetryContext.Provider value={contextValue}>
      {loggedIn ? <>{children}</> : <LoadingScreen />}
    </TelemetryContext.Provider>
  );
};

export const useTelemetry = (): TelemetryContextProps => {
  const context = useContext(TelemetryContext);
  if (!context) {
    throw new Error("useTelemetry must be used within a TelemetryProvider");
  }
  return context;
};
